import axios from "axios";
import { API_URL } from "configs";

export const PAF_ENGINE_PATHS = Object.freeze({
  dashboard: "appraisals/paf-settings/dashboard",
  simulate: "appraisals/paf-settings/simulate",
  generate: "appraisals/paf-settings/generate",
  runs: "appraisals/paf-settings/runs",
  logs: "appraisals/paf-settings/logs",
  globalSettings: "appraisals/paf-global-settings",
  policies: "appraisals/paf-generation-policies",
  policyById: (id) => `appraisals/paf-generation-policies/${id}`,
  appraisalTypeOptions: "appraisals/appraisal-types/options",
});

export const STRATEGY_OPTIONS = [
  { value: "company_schedule", label: "Company Schedule" },
  { value: "hire_date", label: "Hire Date" },
];

export const FREQUENCY_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

export const emptyDeadlineOffsets = () => ({
  monthly: { scoring_offset_days: "", approval_offset_days: "" },
  quarterly: { scoring_offset_days: "", approval_offset_days: "" },
  yearly: { scoring_offset_days: "", approval_offset_days: "" },
});

/**
 * Normalize API/UI deadline_offsets into integers or empty strings for inputs.
 * Does not invent example day counts.
 */
export const normalizeDeadlineOffsets = (raw) => {
  const base = emptyDeadlineOffsets();
  if (!raw || typeof raw !== "object") {
    return base;
  }

  FREQUENCY_OPTIONS.forEach(({ value: frequency }) => {
    const bucket = raw[frequency];
    if (!bucket || typeof bucket !== "object") {
      return;
    }
    base[frequency] = {
      scoring_offset_days: toOffsetInputValue(bucket.scoring_offset_days),
      approval_offset_days: toOffsetInputValue(bucket.approval_offset_days),
    };
  });

  return base;
};

const toOffsetInputValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  if (typeof value === "number" && Number.isInteger(value) && value >= 0) {
    return value;
  }
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    return Number(value.trim());
  }
  return "";
};

/**
 * Client-side validation matching UpdatePafGlobalSettingRequest.
 * @returns {Record<string, string>} field path → message
 */
export const validateDeadlineOffsetsClient = (offsets) => {
  const errors = {};
  FREQUENCY_OPTIONS.forEach(({ value: frequency, label }) => {
    const bucket = offsets?.[frequency];
    if (!bucket || typeof bucket !== "object") {
      errors[`deadline_offsets.${frequency}`] = `${label} deadline offsets are required.`;
      return;
    }
    ["scoring_offset_days", "approval_offset_days"].forEach((key) => {
      const value = bucket[key];
      const path = `deadline_offsets.${frequency}.${key}`;
      if (value === null || value === undefined || value === "") {
        errors[path] = `${label} ${key.replace(/_/g, " ")} is required.`;
        return;
      }
      if (typeof value === "number") {
        if (!Number.isInteger(value) || value < 0) {
          errors[path] = `${label} ${key.replace(/_/g, " ")} must be a whole number of days (0 or greater).`;
        }
        return;
      }
      if (typeof value === "string") {
        if (!/^\d+$/.test(value.trim())) {
          errors[path] = `${label} ${key.replace(/_/g, " ")} must be a whole number of days (0 or greater).`;
        }
        return;
      }
      errors[path] = `${label} ${key.replace(/_/g, " ")} must be a whole number of days (0 or greater).`;
    });
  });
  return errors;
};

export const ELIGIBILITY_OPTIONS = [
  { value: "regular", label: "Regular" },
  { value: "probationary", label: "Probationary" },
  { value: "both", label: "Both" },
];

export const WORKFLOW_OPTIONS = [
  { value: "one_level", label: "One Level" },
  { value: "two_level", label: "Two Level" },
  { value: "three_level", label: "Three Level" },
  { value: "four_level", label: "Four Level" },
];

export const PROBATION_UNIT_OPTIONS = [
  { value: "day", label: "Days" },
  { value: "week", label: "Weeks" },
  { value: "month", label: "Months" },
];

export const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
].map((m) => ({ value: m, label: m }));

const authHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const withObject = (objectViewedId, payload = {}) =>
  objectViewedId ? { ...payload, object_viewed_id: objectViewedId } : payload;

export const fetchEngineDashboard = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${PAF_ENGINE_PATHS.dashboard}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return data;
};

export const fetchGlobalSettings = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${PAF_ENGINE_PATHS.globalSettings}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return data?.data || data;
};

export const updateGlobalSettings = async (payload, objectViewedId) => {
  const { data } = await axios.put(
    `${API_URL}${PAF_ENGINE_PATHS.globalSettings}`,
    withObject(objectViewedId, payload),
    { headers: authHeaders() }
  );
  return data?.data || data;
};

export const fetchPolicies = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${PAF_ENGINE_PATHS.policies}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  const list = Array.isArray(data) ? data : data?.data || [];
  return list;
};

export const createPolicy = async (payload, objectViewedId) => {
  const { data } = await axios.post(
    `${API_URL}${PAF_ENGINE_PATHS.policies}`,
    withObject(objectViewedId, payload),
    { headers: authHeaders() }
  );
  return data?.data || data;
};

export const updatePolicy = async (id, payload, objectViewedId) => {
  const { data } = await axios.put(
    `${API_URL}${PAF_ENGINE_PATHS.policyById(id)}`,
    withObject(objectViewedId, payload),
    { headers: authHeaders() }
  );
  return data?.data || data;
};

export const deletePolicy = async (id, objectViewedId) => {
  await axios.delete(`${API_URL}${PAF_ENGINE_PATHS.policyById(id)}`, {
    headers: authHeaders(),
    data: withObject(objectViewedId),
  });
};

export const fetchAppraisalTypeOptions = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${PAF_ENGINE_PATHS.appraisalTypeOptions}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  const list = Array.isArray(data) ? data : data?.data || [];
  return list.map((row) => ({
    value: row.id || row.value,
    label: row.name || row.label,
    code: row.code,
  }));
};

export const simulateGeneration = async (payload, objectViewedId) => {
  const { data } = await axios.post(
    `${API_URL}${PAF_ENGINE_PATHS.simulate}`,
    withObject(objectViewedId, payload),
    { headers: authHeaders() }
  );
  return data;
};

export const runGeneration = async (payload, objectViewedId) => {
  const { data } = await axios.post(
    `${API_URL}${PAF_ENGINE_PATHS.generate}`,
    withObject(objectViewedId, payload),
    { headers: authHeaders() }
  );
  return data;
};

export const fetchGenerationLogs = async (objectViewedId, filters = {}) => {
  const { data } = await axios.get(`${API_URL}${PAF_ENGINE_PATHS.logs}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId, filters),
  });
  return data?.data || [];
};

export const fetchGenerationRuns = async (objectViewedId, filters = {}) => {
  const { data } = await axios.get(`${API_URL}${PAF_ENGINE_PATHS.runs}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId, filters),
  });
  return data?.data || [];
};

export const exportEngineConfiguration = async (objectViewedId) => {
  const { data } = await axios.get(`${API_URL}appraisals/paf-settings/export`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return data;
};

export const importEngineConfiguration = async (payload, objectViewedId) => {
  const { data } = await axios.post(
    `${API_URL}appraisals/paf-settings/import`,
    withObject(objectViewedId, payload),
    { headers: authHeaders() }
  );
  return data;
};

export const fetchPolicyById = async (id, objectViewedId) => {
  const { data } = await axios.get(`${API_URL}${PAF_ENGINE_PATHS.policyById(id)}`, {
    headers: authHeaders(),
    params: withObject(objectViewedId),
  });
  return data?.data || data;
};

export const getErrorMessage = (error) => {
  const response = error?.response;
  if (response?.status === 422) {
    const errors = response.data?.errors;
    if (errors && typeof errors === "object") {
      const firstKey = Object.keys(errors)[0];
      const firstMessage = firstKey ? errors[firstKey]?.[0] : null;
      if (firstMessage) return String(firstMessage);
    }
    return response.data?.message || "Validation failed.";
  }
  return (
    response?.data?.message ||
    response?.data?.error ||
    error?.message ||
    "Something went wrong."
  );
};
